import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaInTextAuthors,
  apaName,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  mlaAuthors,
  mlaInTextAuthors,
  naturalName,
  type PartialDate,
  type PersonName,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  apaPageLocator,
  mlaPageLocator,
  pageEnds,
  readDate,
} from "@/lib/tool-kit/citation-sources-books/common";
import {
  shortNoteTitle,
  titleCase,
} from "@/lib/tool-kit/citation-sources-more/common";
import { editionLabel } from "@/lib/tool-kit/citation-sources-more/lab-manual-citation-generator";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  type MediaLabels,
  type MediaValues,
  mlaRoleNames,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";
import {
  apaContributors,
  chicagoBibliographyContributors,
  chicagoNoteContributors,
  chicagoPageRange,
  mlaContributors,
  publisherName,
} from "@/lib/tool-kit/special-sources/common";

/*
 * A foreword, preface, introduction or afterword written by someone other than the
 * book's author, in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7: the APA Style site's advice on a chapter written by someone other than the
 *   book's authors (read through APA Style's search summary and the Wintec and Morling
 *   College guides that reproduce it): do not cite the foreword as a chapter of an
 *   authored book — cite the whole book and name the foreword's writer in your text.
 *   A writer credited on the cover with "with" goes in the reference in parentheses:
 *   "Kübler-Ross, E. (with Byock, I.). (2014). On death & dying (50th anniversary
 *   ed.). Scribner. (Original work published 1969)". When you cite the foreword itself,
 *   the in-text citation names its writer: (Kübler-Ross, 1969/2014, foreword by Byock,
 *   p. xv). Translator and edition share the parentheses after the title with a
 *   semicolon, translator first — a library-guide convention (Curtin University), not
 *   a published APA example.
 * - MLA 9: "Sears, Barry. Afterword. The Jungle, by Upton Sinclair. Signet, 2001, pp.
 *   343-47." (University of Lethbridge guide; the label changes to Foreword,
 *   Introduction or Preface), the page numbers being the part's, and in text (Sears
 *   344). With several introductions, a description replaces the label: "Knox,
 *   Bernard. Introduction to Antigone. The Three Theban Plays, by Sophocles,
 *   translated by Robert Fagles, Penguin Books, 2000, pp. 33-54." (MLA Style Center).
 *   For the book itself the introducer may be dropped or named under other
 *   contributors: "…, translated by Richard Pevear and Larissa Volkhonsky,
 *   introduction by Pevear, Vintage Books, 1996." (MLA Style Center). When the part's
 *   writer is also the book's author, only the surname follows "by" (Macmillan).
 * - Chicago 18, read through the University of Portland guide: the part's name is
 *   neither italic nor in quotation marks and the writer's name starts the entry:
 *   "Sacks, Oliver. Foreword to A Man without Words, by Susan Schaller. 2nd ed.
 *   University of California Press, 2012." and the note "Oliver Sacks, foreword to A
 *   Man without Words, by Susan Schaller, 2nd ed. (University of California Press,
 *   2012), 10." Chicago 18 no longer asks for the part's page range in a
 *   bibliography. The shortened note, "Sacks, foreword, 10.", follows the pattern of
 *   the 17th-edition guides (Ursuline College: "Green, afterword, 810-11.").
 */

export const FOREWORD_FIELDS = {
  part: "part",
  writers: "writers",
  authors: "authors",
  title: "title",
  edition: "edition",
  translators: "translators",
  editors: "editors",
  publisher: "publisher",
  year: "year",
  originalYear: "originalYear",
  partPages: "partPages",
  page: "page",
  description: "description",
  credited: "credited",
  shortTitle: "shortTitle",
} as const;

const F = FOREWORD_FIELDS;

/** The kinds of front or back matter this tool cites. */
export type ForewordPart =
  | "foreword"
  | "introduction"
  | "preface"
  | "afterword";

const PARTS: readonly ForewordPart[] = [
  "foreword",
  "introduction",
  "preface",
  "afterword",
];

export interface ForewordLabels extends MediaLabels {
  worksCitedBook: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Piece {
  part: ForewordPart;
  writers: PersonName[];
  authors: PersonName[];
  title: string;
  edition: string;
  translators: PersonName[];
  editors: PersonName[];
  publisher: string;
  year: PartialDate | null;
  originalYear: PartialDate | null;
  partPages: string;
  page: string;
  description: string;
  credited: boolean;
  shortTitle: string;
}

function readPart(text: string): ForewordPart {
  const found = PARTS.find((part) => part === text);
  return found ?? "foreword";
}

function readPiece(values: MediaValues): Piece {
  return {
    part: readPart(get(values, F.part)),
    writers: parsePeople(get(values, F.writers)),
    authors: parsePeople(get(values, F.authors)),
    title: capitalizeSubtitle(get(values, F.title)),
    edition: editionLabel(get(values, F.edition)),
    translators: parsePeople(get(values, F.translators)),
    editors: parsePeople(get(values, F.editors)),
    publisher: get(values, F.publisher),
    year: readDate(get(values, F.year)),
    originalYear: readDate(get(values, F.originalYear)),
    partPages: get(values, F.partPages),
    page: get(values, F.page),
    description: get(values, F.description),
    credited: get(values, F.credited) === "true",
    shortTitle: get(values, F.shortTitle),
  };
}

function key(name: PersonName): string {
  return `${name.given.join(" ")} ${name.family}`.trim().toLowerCase();
}

/** True when the writer of the part and the author of the book are the same people. */
function sameWriter(piece: Piece): boolean {
  return (
    piece.writers.length > 0 &&
    piece.writers.length === piece.authors.length &&
    piece.writers.every(
      (name, index) => key(name) === key(piece.authors[index])
    )
  );
}

function capitalized(text: string): string {
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

function yearNumber(date: PartialDate | null): string {
  return date ? String(date.year) : "n.d.";
}

/* ---------------------------------------------------------------------- APA */

function apaYearText(piece: Piece): string {
  const year = yearNumber(piece.year);
  return piece.originalYear ? `${piece.originalYear.year}/${year}` : year;
}

function apaLead(piece: Piece): string {
  const authors = apaAuthors(piece.authors);
  if (authors === "") {
    return "";
  }
  if (piece.credited && piece.writers.length > 0) {
    const names = piece.writers.map(apaName).join(", ");
    return `${authors} (with ${names}).`;
  }
  return `${authors}${closer(authors)}`;
}

function apaParenthetical(piece: Piece): string {
  const people = apaContributors({
    editors: piece.editors,
    translators: piece.translators,
  });
  const inner = [people, piece.edition].filter(Boolean).join("; ");
  return inner ? ` (${inner})` : "";
}

function apaReference(piece: Piece): CitationParts {
  const year = yearNumber(piece.year);
  const publisher = piece.publisher
    ? ` ${withPeriod(publisherName(piece.publisher, "apa"))}`
    : "";
  const original = piece.originalYear
    ? ` (Original work published ${piece.originalYear.year})`
    : "";
  const lead = apaLead(piece);
  const paren = apaParenthetical(piece);
  if (lead === "") {
    return tidyParts([
      italic(piece.title),
      plain(`${paren}. (${year}).${publisher}${original}`),
    ]);
  }
  return tidyParts([
    plain(`${lead} (${year}). `),
    italic(piece.title),
    plain(`${paren}.${publisher}${original}`),
  ]);
}

function apaCitation(piece: Piece, narrative: boolean): CitationParts {
  const writer = apaInTextAuthors(piece.writers);
  const by = writer && !sameWriter(piece) ? `${piece.part} by ${writer}` : "";
  const locator = apaPageLocator(piece.page);
  const tail = [apaYearText(piece), by, locator].filter(Boolean).join(", ");
  if (piece.authors.length === 0) {
    const short = titleCase(piece.title).split(":")[0];
    return narrative
      ? [italic(short), plain(` (${tail})`)]
      : [plain("("), italic(short), plain(`, ${tail})`)];
  }
  const who = apaInTextAuthors(piece.authors, narrative);
  return [plain(narrative ? `${who} (${tail})` : `(${who}, ${tail})`)];
}

function apaOutput(piece: Piece, labels: ForewordLabels): CitationOutput {
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(piece) },
      { label: labels.parenthetical, parts: apaCitation(piece, false) },
      { label: labels.narrative, parts: apaCitation(piece, true) },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

/** "by Upton Sinclair"; just the surname when the part's writer wrote the book. */
function mlaBy(piece: Piece): string {
  if (piece.authors.length === 0) {
    return "";
  }
  if (sameWriter(piece)) {
    return `by ${mlaInTextAuthors(piece.authors)}`;
  }
  return `by ${mlaRoleNames(piece.authors.map(naturalName))}`;
}

function lowerFirst(text: string): string {
  return `${text.charAt(0).toLowerCase()}${text.slice(1)}`;
}

function mlaContributorText(piece: Piece): string {
  return mlaContributors({
    editors: piece.editors,
    translators: piece.translators,
  });
}

function mlaPublisherAndDate(piece: Piece): string[] {
  const publisher = piece.publisher
    ? publisherName(piece.publisher, "mla")
    : "";
  return [publisher, piece.year ? String(piece.year.year) : ""];
}

function mlaLabel(piece: Piece): string {
  return piece.description || capitalized(piece.part);
}

function mlaWorksCited(piece: Piece): CitationParts {
  const title = titleCase(piece.title);
  const writers = mlaAuthors(piece.writers);
  const lead = writers ? `${writers}${closer(writers)} ` : "";
  const label = mlaLabel(piece);
  const contributors = lowerFirst(mlaContributorText(piece));
  const tail = [
    mlaBy(piece),
    contributors,
    piece.edition,
    ...mlaPublisherAndDate(piece),
    mlaPageLocator(piece.partPages),
  ]
    .filter(Boolean)
    .join(", ");
  return tidyParts([
    plain(`${lead}${label}${closer(label)} `),
    italic(title),
    plain(`${tail ? `, ${tail}` : ""}.`),
  ]);
}

/** The book itself, with the part's writer named under other contributors. */
function mlaBookEntry(piece: Piece): CitationParts {
  const title = titleCase(piece.title);
  const authors = mlaAuthors(piece.authors);
  const lead = authors ? `${authors}${closer(authors)} ` : "";
  const writers = mlaRoleNames(piece.writers.map(naturalName));
  const credit =
    writers && !sameWriter(piece) ? `${piece.part} by ${writers}` : "";
  const tail = [
    mlaContributorText(piece),
    credit,
    piece.edition,
    ...mlaPublisherAndDate(piece),
  ]
    .filter(Boolean)
    .join(", ");
  return tidyParts([
    plain(lead),
    italic(title),
    plain(`${closer(title)}${tail ? ` ${withPeriod(capitalized(tail))}` : ""}`),
  ]);
}

function mlaOutput(piece: Piece, labels: ForewordLabels): CitationOutput {
  const page = piece.page ? pageEnds(piece.page)[0] : "";
  const who =
    piece.writers.length > 0
      ? mlaInTextAuthors(piece.writers)
      : titleCase(piece.title);
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: mlaWorksCited(piece) },
      {
        label: labels.inText,
        parts: [plain(`(${who}${page ? ` ${page}` : ""})`)],
      },
      { label: labels.worksCitedBook, parts: mlaBookEntry(piece) },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function chicagoPages(text: string): string {
  const [from, to] = pageEnds(text);
  return to ? chicagoPageRange(from, to) : from;
}

function chicagoImprint(piece: Piece): string {
  const publisher = piece.publisher
    ? publisherName(piece.publisher, "chicago")
    : "";
  return [publisher, yearNumber(piece.year)].filter(Boolean).join(", ");
}

/** "by Susan Schaller" (the book's author in full), or nothing if the writer is the author. */
function chicagoBy(piece: Piece): string {
  if (piece.authors.length === 0 || sameWriter(piece)) {
    return "";
  }
  return `by ${chicagoNoteAuthors(piece.authors)}`;
}

function chicagoNote(piece: Piece, title: string): CitationParts {
  const writers = chicagoNoteAuthors(piece.writers);
  const lead = writers ? `${writers}, ` : "";
  const contributors = chicagoNoteContributors({
    editors: piece.editors,
    translators: piece.translators,
  });
  const after = [chicagoBy(piece), contributors, piece.edition]
    .filter(Boolean)
    .join(", ");
  const page = piece.page ? `, ${chicagoPages(piece.page)}` : "";
  return tidyParts([
    plain(`${lead}${piece.part} to `),
    italic(title),
    plain(`${after ? `, ${after}` : ""} (${chicagoImprint(piece)})${page}.`),
  ]);
}

function chicagoShortNote(piece: Piece, title: string): CitationParts {
  const writers = chicagoShortAuthors(piece.writers);
  const page = piece.page ? `, ${chicagoPages(piece.page)}` : "";
  if (writers === "") {
    const short = piece.shortTitle || shortNoteTitle(title);
    return tidyParts([italic(short), plain(`, ${piece.part}${page}.`)]);
  }
  return [plain(`${writers}, ${piece.part}${page}.`)];
}

function chicagoBibliography(piece: Piece, title: string): CitationParts {
  const writers = chicagoBibliographyAuthors(piece.writers);
  const lead = writers ? `${writers}${closer(writers)} ` : "";
  const by = chicagoBy(piece);
  const contributors = chicagoBibliographyContributors({
    editors: piece.editors,
    translators: piece.translators,
  });
  const rest = [contributors, piece.edition, `${chicagoImprint(piece)}.`]
    .filter(Boolean)
    .join(" ");
  return tidyParts([
    plain(`${lead}${capitalized(piece.part)} to `),
    italic(title),
    plain(`${by ? `, ${by}` : ""}${closer(title)} ${rest}`),
  ]);
}

function chicagoOutput(piece: Piece, labels: ForewordLabels): CitationOutput {
  const title = titleCase(piece.title);
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoNote(piece, title) },
      { label: labels.shortNote, parts: chicagoShortNote(piece, title) },
      { label: labels.bibliography, parts: chicagoBibliography(piece, title) },
    ],
    note: labels.chicagoNote,
  };
}

export function forewordIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function formatForeword(
  values: MediaValues,
  labels: ForewordLabels
): CitationOutput[] {
  const piece = readPiece(values);
  return [
    apaOutput(piece, labels),
    mlaOutput(piece, labels),
    chicagoOutput(piece, labels),
  ];
}
